import { assoc, memoizeReduxState, pick } from '@range.io/functional'
import { _field } from '../../selector-primitives.js'
import UploadSelectors from '../upload-slice/upload-selectors.js'

/*
 * Return the TagName with the given text or undefined
 * @sig :: tagNameForName :: (State, String) -> TagName|undefined
 */
const tagNameForName = (state, name) => {
    const tagNames = Object.values(state.tagNames)
    return tagNames.find(t => t.name === name)
}

/*
 * Return all TagNames enriched with their count of usage across the app
 * @sig :: tagDataPropTypes :: (State) => {...TagName, Number}
 */
const enrichedTagNames = state => {
    const allTagNames = _field('tagNames')(state)
    const allUploads = UploadSelectors.uploadsAsPOJO(state)
    const allCollaborations = _field('collaborations')(state)
    const counts = {}

    Object.keys(allTagNames).forEach(tagId => (counts[tagId] = 0))
    Object.values(allUploads).forEach(upload => upload.tagIds.forEach(tagId => (counts[tagId] += 1)))
    Object.values(allCollaborations).forEach(collaboration => collaboration.tagNameIds.forEach(id => (counts[id] += 1)))

    const newAllTagNames = {}
    Object.values(allTagNames).forEach(tag => {
        newAllTagNames[tag.id] = { ...tag }
        newAllTagNames[tag.id].count = counts[tag.id]
    })
    return newAllTagNames
}

/*
 * Return all TagNames enriched with their count of usage across the app
 * @sig :: computeTagCountData :: State -> { TagId: CountData }
 */
const _computeCountData = state => {
    // :: ({ Id: CountData }, TagName) -> { Id: CountData }
    const initializeCountData = (acc, tn) => {
        acc[tn.id] = { id: tn.id, name: tn.name, count: 0, canvasCounts: { ...initialCanvasCounts } }
        return acc
    }

    // Impure: updates both the overall count and the count for the specific canvasId
    const incrementTagCounts = (canvasId, tagId) => {
        countData[tagId].count++
        countData[tagId].canvasCounts[canvasId]++
    }

    // Increment the count for each tagId in a Collaboration
    const incrementTagCountsForCollaboration = c => c.tagNameIds.forEach(id => incrementTagCounts(c.canvasId, id))

    // Increment the count for each tagId in an Upload
    const incrementTagCountsForUpload = u => u.tagIds.forEach(tagId => incrementTagCounts(u.canvasId, tagId))

    const collaborations = Object.values(state.collaborations)
    const uploads = Object.values(state.uploads)
    const canvasKeys = Object.keys(state.canvases)

    const initialCanvasCounts = canvasKeys.reduce((acc, id) => assoc(id, 0, acc), {})
    const countData = Object.values(state.tagNames).reduce(initializeCountData, {})

    // update tag counts for each Upload and Collaboration
    collaborations.forEach(incrementTagCountsForCollaboration)
    uploads.forEach(incrementTagCountsForUpload)

    return countData
}

const computeTagCountData = memoizeReduxState(['canvases', 'collaborations', 'tagNames', 'uploads'], _computeCountData)

const tagNamesForCollaboration = (state, collaboration) => Object.values(pick(collaboration.tagNameIds, state.tagNames))

const tagNamesForUpload = (state, upload) => {
    const allTags = state.tagNames
    return upload.tagIds.map(id => allTags[id])
}

// ---------------------------------------------------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------------------------------------------------

const TagNameSelectors = {
    enrichedTagNames,
    tagNameForName,
    tagNamesAsPOJO: _field('tagNames'),
    tagNamesForCollaboration,
    tagNamesForUpload,
    computeTagCountData,
}

export default TagNameSelectors
